import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './BookStore.css';
import API_BASE_URL, { API_ENDPOINTS } from '../../config/api';
import LogoLoader from '../../components/LogoLoader';
import { getDominantColor } from '../../utils/colorExtractor';
import axios from 'axios';
import { robustFetch } from '../../utils/apiUtils';
import { getAuthToken } from '../../utils/auth';

const BookCard = ({ book }) => {
    const [borderColor, setBorderColor] = useState('rgba(0,0,0,0.08)');

    useEffect(() => {
        if (book.productImages && book.productImages.length > 0) {
            getDominantColor(book.productImages[0].url).then(color => {
                setBorderColor(color);
            });
        }
    }, [book]);

    return (
        <Link 
            to={`/app-store/bookstore/${book._id}`} 
            className="book-card"
            style={{ borderColor: borderColor }}
        >
            <div className="book-image-wrapper">
                {book.productImages && book.productImages.length > 0 ? (
                    <img 
                        src={book.productImages[0].url} 
                        alt={book.productImages[0].altText || book.productName} 
                        className="book-image"
                    />
                ) : (
                    <div className="book-image-placeholder">
                        <i className="fi fi-rr-book"></i>
                    </div>
                )}
            </div>
        </Link>
    );
};

const BookStore = () => {
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [error, setError] = useState(null);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        const fetchSubscriptionStatus = async () => {
            const token = getAuthToken();
            if (!token) return;
            try {
                const response = await axios.get(API_ENDPOINTS.USER.PROFILE, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.data && response.data.success) {
                    setIsSubscribed(response.data.data.isSubscribed);
                }
            } catch (err) {
                console.error('Failed to fetch user profile:', err);
            }
        };

        const fetchBooks = async () => {
            try {
                const response = await robustFetch(API_ENDPOINTS.AFFILIATE_PRODUCTS.BOOKS);
                const result = await response.json();
                if (result.success) {
                    setBooks(result.data);
                } else {
                    setError(result.message || 'Failed to fetch books');
                }
            } catch (err) {
                setError('Failed to load books. Please try again later.');
                console.error('Fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchSubscriptionStatus();
        fetchBooks();
    }, []);

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const token = getAuthToken();
        if (!token) {
            alert('Please sign in to upload books');
            return;
        }

        // Client side validation
        const limit = isSubscribed ? 50 * 1024 * 1024 : 25 * 1024 * 1024;
        if (file.size > limit) {
            alert(`File too large. Max ${isSubscribed ? '50MB' : '25MB'} allowed.`);
            return;
        }

        if (file.type !== 'application/pdf') {
            alert('Only PDF files are allowed');
            return;
        }

        const formData = new FormData();
        formData.append('book', file);

        setUploading(true);
        try {
            const response = await axios.post(API_ENDPOINTS.OWN_BOOKS.UPLOAD, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data.success) {
                alert('Book uploaded successfully!');
            }
        } catch (err) {
            console.error('Upload failed:', err);
            const msg = err.response?.data?.message || 'Upload failed. Please try again.';
            alert(msg);
        } finally {
            setUploading(false);
            // Clear input
            e.target.value = '';
        }
    };

    const filteredBooks = useMemo(() => {
        if (!searchQuery.trim()) return books;
        const query = searchQuery.toLowerCase();
        return books.filter(book => 
            book.productName.toLowerCase().includes(query) ||
            (book.productDescription && typeof book.productDescription === 'string' && book.productDescription.toLowerCase().includes(query))
        );
    }, [books, searchQuery]);

    if (loading) {
        return <LogoLoader progress={50} />;
    }

    return (
        <div className="bookstore-page">
            <Helmet>
                <title>Book Store | Digital Library | Hari Vishnu</title>
                <meta name="description" content="Discover amazing digital books, guides, and resources curated by Hari Vishnu." />
            </Helmet>

            <div className="bookstore-header">
                <div className="bookstore-header-content">
                    <h1 className="bookstore-title">Book Store</h1>
                    <p className="bookstore-subtitle">Discover Your Next Digital Adventure</p>
                    
                    <div className="bookstore-search-container">
                        <div className="bookstore-search-bar">
                            <i className="fi fi-rr-search search-icon"></i>
                            <input 
                                type="text" 
                                placeholder="Search for books, topics, or authors..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="bookstore-search-input"
                            />
                            {searchQuery && (
                                <button className="clear-search" onClick={() => setSearchQuery('')}>
                                    <i className="fi fi-rr-cross-small"></i>
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="read-own-book-container mt-8">
                        <div className="read-own-book-badge mb-3">Personal Library</div>
                        <div className="read-own-book-actions flex flex-wrap justify-center gap-4">
                            <label className="read-own-btn add-btn cursor-pointer">
                                <i className="fi fi-sr-add"></i>
                                <span>Add Book</span>
                                <input 
                                    type="file" 
                                    accept=".pdf" 
                                    onChange={handleFileUpload} 
                                    className="hidden" 
                                    disabled={uploading}
                                />
                            </label>
                            <Link to="/app-store/bookstore/own-books" className="read-own-btn lib-btn">
                                <i className="fi fi-sr-library"></i>
                                <span>Library</span>
                            </Link>
                        </div>
                        <p className="read-own-hint mt-3">
                            {isSubscribed 
                                ? "Premium: Upload PDFs up to 50MB. Unlimited storage." 
                                : "Free: Upload PDFs up to 25MB. Limit: 1 book."}
                        </p>
                        {uploading && <div className="upload-progress-text mt-2 text-white/80 animate-pulse">Uploading your book...</div>}
                    </div>
                </div>
            </div>

            <div className="bookstore-container">
                {error ? (
                    <div className="bookstore-error">
                        <i className="fi fi-rr-exclamation"></i>
                        <p>{error}</p>
                    </div>
                ) : filteredBooks.length === 0 ? (
                    <div className="bookstore-empty">
                        <i className="fi fi-rr-book-open-reader"></i>
                        <p>{searchQuery ? `No books found for "${searchQuery}"` : "Our digital library is coming soon!"}</p>
                    </div>
                ) : (
                    <div className="bookstore-grid">
                        {filteredBooks.map((book) => (
                            <BookCard key={book._id} book={book} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BookStore;
